import { useState, useCallback } from 'react';
import collectionApiClient from '../../../api/collections';
import useUpsertCollectionMutation from './useUpsertCollectionMutation';
import { useShopifyPreviewUrlQuery } from './useShopifyPreviewUrlQuery';
import { CollectionReviewStates } from '../../../api/collections';
import {
  COLLECTION_NAME_TAKEN_ALERT,
  SUBMIT_COLLECTION_FOR_REVIEW_ALERT,
} from '../../../consts/alerts';
import useAnalytics from '../../../hooks/useAnalytics';

const usePublishCollection = (
  collectionId: string | undefined,
  collectionName: string,
  options?: {
    onBeforeOpen?: () => boolean;
    onSuccess?: () => void;
    comment?: string;
  },
) => {
  const [warningDialogOpen, setWarningDialogOpen] = useState(false);
  const [isNameTaken, setIsNameTaken] = useState(false);
  const { mutate: updateCollectionMutation } = useUpsertCollectionMutation();
  const { refetch: fetchPreviewUrl } = useShopifyPreviewUrlQuery(collectionId);
  const { trackEvent } = useAnalytics();

  const handlePublishClick = useCallback(async () => {
    if (options?.onBeforeOpen && !options.onBeforeOpen()) return;

    if (collectionId && collectionName) {
      const { available } = await collectionApiClient.checkNameAvailable(
        collectionId,
        collectionName,
      );
      setIsNameTaken(!available);
    }

    setWarningDialogOpen(true);
  }, [collectionId, collectionName, options]);

  const handleWarningConfirm = useCallback(() => {
    setWarningDialogOpen(false);
    if (!collectionId) return;
    updateCollectionMutation(
      {
        collectionId,
        collection: {
          reviewStatus: {
            state: CollectionReviewStates.Pending,
            message: '',
            comment: options?.comment?.trim() ?? '',
          },
        },
      },
      {
        onSuccess: () => {
          trackEvent({
            type: 'collection_published',
            context: `collectionId:${collectionId}`,
          });
          fetchPreviewUrl();
          options?.onSuccess?.();
        },
      },
    );
  }, [collectionId, updateCollectionMutation, fetchPreviewUrl, options]);

  const handleWarningClose = useCallback(() => {
    setWarningDialogOpen(false);
  }, []);

  return {
    warningDialogOpen,
    isNameTaken,
    handlePublishClick,
    handleWarningConfirm,
    handleWarningClose,
    warningMessage: SUBMIT_COLLECTION_FOR_REVIEW_ALERT,
    nameWarningNote: isNameTaken ? COLLECTION_NAME_TAKEN_ALERT : undefined,
  };
};

export default usePublishCollection;
